// src/components/DockedCallBar.jsx
import React, { useMemo, useEffect, useState, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MemoryRouter, Route, useHistory } from 'react-router-dom';
import {
  Button,
  Icon,
  LoaderCircle,
} from 'rhinostyle';
import { useVoip } from '../voip/VoipProvider';
import { formatPhone } from '../helpers/PhoneHelpers';
import { fetchUser } from '../reducers/userReducer';
import { setActiveSavedContentItemId } from '../reducers/savedContentReducer';

const ContactProfilePanel = React.lazy(() => import('./ContactProfilePanel'));
const ContactLibraryPanel = React.lazy(() => import('./ContactLibraryPanel'));
const SavedContentPanel = React.lazy(() => import('./SavedContentPanel'));

class CallPanelErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.warn('Call panel profile error:', error.message); // eslint-disable-line no-console
  }

  render() {
    if (this.state.hasError) {
      return <p className="u-text-center u-text-muted u-p-a">Unable to load contact profile</p>;
    }
    return this.props.children; // eslint-disable-line react/prop-types
  }
}

const DockedCallBar = () => {
  const dispatch = useDispatch();
  const browserHistory = useHistory();
  const [isExpanded, setIsExpanded] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [activePanel, setActivePanel] = useState('profile');
  const [savedContentItemId, setSavedContentItemId] = useState(null);
  const [viewedContactId, setViewedContactId] = useState(null);
  const [contactHistoryStack, setContactHistoryStack] = useState([]);

  const {
    callPhase,
    incomingCall,
    activeCall,
    answer,
    reject,
    hangup,
    toggleMute,
    toggleHold,
    isMuted,
    isHeld,
    callContact,
    contactLookupDone,
    incomingContact,
    isCallWaiting,
  } = useVoip();

  // The call in progress owns the panel; a call ringing on top of it is shown as a waiting banner.
  const currentCall = activeCall || incomingCall;
  const hasCall = currentCall && callPhase !== 'idle' && callPhase !== 'ended';

  const baseContactId = callContact?.id;
  const contactId = viewedContactId || baseContactId;

  // Load full user into Redux store
  const allUsers = useSelector((state) => state.user.users);
  useEffect(() => {
    if (contactId) {
      if (!allUsers[contactId]?.organization) {
        dispatch(fetchUser(contactId));
      }
    }
  }, [contactId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Call duration timer; restarts when a different session becomes the active call (call waiting).
  const activeCallId = activeCall?.id;
  useEffect(() => {
    let interval;
    if (callPhase === 'active') {
      setCallDuration(0);
      interval = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callPhase, activeCallId]);

  // Auto-expand on new call
  useEffect(() => {
    if (hasCall) {
      setIsExpanded(true);
      setActivePanel('profile');
      setViewedContactId(null);
      setContactHistoryStack([]);
    }
  }, [hasCall]);

  // Check if the full user is loaded in Redux
  const fullUser = useSelector((state) => (contactId ? state.user.users[contactId] : null));
  const isFullUserLoaded = fullUser && fullUser.organization !== undefined;
  const hasContactHistory = contactHistoryStack.length > 0;

  const handleViewContact = (userId) => {
    setContactHistoryStack((prev) => [...prev, contactId]);
    setViewedContactId(userId);
    setActivePanel('profile');
  };

  const handleBackContact = () => {
    const prev = [...contactHistoryStack];
    const previousId = prev.pop();
    setContactHistoryStack(prev);
    setViewedContactId(previousId === baseContactId ? null : previousId);
    setActivePanel('profile');
  };

  const contactName = callContact
    ? [callContact.firstName, callContact.lastName].filter(Boolean).join(' ')
    : null;

  const callerDisplay =
    contactName ||
    currentCall?.remoteIdentity?.displayName ||
    currentCall?._remoteDisplayName ||
    currentCall?._outgoingTarget ||
    'Unknown';

  const callerNumber = useMemo(() => {
    if (currentCall?._outgoingTarget) {
      return currentCall._outgoingTarget;
    }
    const callerUri = currentCall?.remoteIdentity?.uri?.toString() || '';
    return callerUri.replace(/^sip:([^@]+)@.*$/, '$1');
  }, [currentCall]);

  const phaseLabel = callPhase === 'ringing' ? 'Incoming Call' : 'Active Call';
  const formattedNumber = formatPhone(callerNumber) || callerNumber;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Waiting caller (second call ringing during this one)
  const waitingNumber = (incomingCall?.remoteIdentity?.uri?.toString() || '').replace(/^sip:([^@]+)@.*$/, '$1');
  const waitingName = incomingContact
    ? [incomingContact.firstName, incomingContact.lastName].filter(Boolean).join(' ')
    : null;
  const waitingDisplay = waitingName || incomingCall?.remoteIdentity?.displayName || formatPhone(waitingNumber) || waitingNumber || 'Unknown';
  const waitingBanner = isCallWaiting && (
    <div className="call-waiting">
      <div className="call-waiting__info">
        <span className="call-waiting__label">Incoming call</span>
        <span className="call-waiting__name">{waitingDisplay}</span>
        {waitingName && waitingNumber && (
          <span className="call-waiting__number">{formatPhone(waitingNumber) || waitingNumber}</span>
        )}
      </div>
      <div className="call-waiting__actions">
        <Button type="secondary" size="small" onClick={answer} title="Answer and put the current call on hold">Answer</Button>
        <Button type="danger" size="small" onClick={reject} title="Send to voicemail">Decline</Button>
      </div>
    </div>
  );

  if (!hasCall) return null;

  return (
    <>
      {/* Audio element lives outside both views so it never unmounts during minimize/expand */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio id="remoteAudio" autoPlay playsInline />

      {/* Minimized bottom bar */}
      {!isExpanded && (
        <div className="call-bar-mini">
          {waitingBanner}
          <div className="call-bar-mini__left" onClick={() => setIsExpanded(true)}>
            <span className="call-bar-mini__indicator" />
            <div className="call-bar-mini__info">
              <span className="call-bar-mini__name">{callerDisplay}</span>
              {callerNumber && callerDisplay !== callerNumber && (
                <span className="call-bar-mini__number">{formattedNumber}</span>
              )}
            </div>
            {callPhase === 'active' && (
              <span className="call-bar-mini__duration">{formatDuration(callDuration)}</span>
            )}
            {callPhase === 'ringing' && (
              <span className="call-bar-mini__ringing">Ringing...</span>
            )}
          </div>
          <div className="call-bar-mini__actions">
            {callPhase === 'ringing' && (
              <>
                <Button type="secondary" size="small" onClick={answer}>Answer</Button>
                <Button type="danger" size="small" onClick={reject}>Reject</Button>
              </>
            )}
            {callPhase === 'active' && (
              <>
                <Button
                  type={isMuted ? 'default' : 'input'}
                  size="small"
                  onClick={toggleMute}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
                <Button type="danger" size="small" onClick={hangup}>End</Button>
              </>
            )}
            <Button type="link" size="small" onClick={() => setIsExpanded(true)}>
              <Icon icon="caret-up" />
            </Button>
          </div>
        </div>
      )}

      {/* Expanded right-side panel */}
      {isExpanded && (
      <div className="call-panel is-open">
        {/* Header */}
        <div className="call-panel__header">
          {(activePanel !== 'profile' || hasContactHistory) ? (
            <Button
              type="link"
              size="small"
              onClick={() => {
                if (activePanel === 'savedContent') {
                  setActivePanel('library');
                  setSavedContentItemId(null);
                } else if (activePanel === 'library') {
                  setActivePanel('profile');
                } else {
                  handleBackContact();
                }
              }}
            >
              <Icon icon="caret-left" /> Back
            </Button>
          ) : (
            <span className="call-panel__header__phase">
              {phaseLabel}
              {callPhase === 'active' && (
                <span className="call-panel__header__duration"> — {formatDuration(callDuration)}</span>
              )}
            </span>
          )}
          <Button type="link" iconOnly size="small" onClick={() => setIsExpanded(false)} className="call-panel__header__close">
            <Icon icon="close" />
          </Button>
        </div>

        {waitingBanner}

        {/* Full Contact Profile Panel */}
        {contactId && isFullUserLoaded && (
          <div className="call-panel__profile">
            <CallPanelErrorBoundary>
              <Suspense fallback={<div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>}>
                <MemoryRouter key={contactId} initialEntries={[`/contact-panel/${contactId}`]}>
                  <Route path="/contact-panel/:userId">
                    {activePanel === 'profile' && (
                      <ContactProfilePanel
                        activeUser={fullUser}
                        isActivePanel
                        showGoToConversation
                        hideRhinopay
                        history={browserHistory}
                        onViewContact={handleViewContact}
                        setOpenContactLibraryPanel={() => setActivePanel('library')}
                      />
                    )}
                    {activePanel === 'library' && (
                      <ContactLibraryPanel
                        isActivePanel
                        contactId={contactId}
                        handleCloseSavedContentLibrary={() => setActivePanel('profile')}
                        onItemSelect={(id) => {
                          setSavedContentItemId(id);
                          dispatch(setActiveSavedContentItemId(id));
                          setActivePanel('savedContent');
                        }}
                      />
                    )}
                    {activePanel === 'savedContent' && savedContentItemId && (
                      <SavedContentPanel
                        onClose={() => {
                          setSavedContentItemId(null);
                          setActivePanel('library');
                        }}
                      />
                    )}
                  </Route>
                </MemoryRouter>
              </Suspense>
            </CallPanelErrorBoundary>
          </div>
        )}

        {contactId && !isFullUserLoaded && (
          <div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>
        )}

        {!contactId && !contactLookupDone && (
          <div className="u-text-center u-p-a"><LoaderCircle size="small" /></div>
        )}

        {!contactId && contactLookupDone && (
          <div className="call-panel__no-contact">
            <p className="u-text-center u-text-muted u-p-a">No contact found for this number</p>
          </div>
        )}

        {/* Call Controls */}
        <div className="call-panel__controls">
          {callPhase === 'ringing' ? (
            <>
              <Button type="secondary" onClick={answer} className="call-panel__btn">
                <Icon icon="phone" /> Answer
              </Button>
              <Button type="danger" onClick={reject} className="call-panel__btn">
                <Icon icon="phone" /> Reject
              </Button>
            </>
          ) : (
            <>
              <Button
                type={isMuted ? 'default' : 'input'}
                onClick={toggleMute}
                className="call-panel__btn"
              >
                {isMuted ? 'Unmute' : 'Mute'}
              </Button>
              <Button
                type={isHeld ? 'default' : 'input'}
                onClick={toggleHold}
                className="call-panel__btn"
              >
                {isHeld ? 'Resume' : 'Hold'}
              </Button>
              <Button type="danger" onClick={hangup} className="call-panel__btn">
                Hang Up
              </Button>
            </>
          )}
        </div>

      </div>
      )}

    </>
  );
};

export default DockedCallBar;
